import { config } from '../components/constants.js';
import { recordAction, setAction } from '../components/infrastructure/recordProgress.js';

const url = config.url.API_URL;

function buildTicketRequestBody(completionState) {
  return {
    skillId: completionState.skillId,
    totalTime: completionState.totalTime,
    questionsCorrect: completionState.questionsCorrect,
    questionsAttempted: completionState.questionsAttempted,
    longestStreak: completionState.longestStreak ?? completionState.questionsStreak ?? 0,
    questionsStreak: completionState.questionsStreak,
    sessionId: completionState.sessionId ?? null,
  };
}

export async function previewSkillTickets({ userId, completionState, isGuest = false }) {
  const body = buildTicketRequestBody(completionState);

  const endpoint = userId && !isGuest
    ? `${url}/users/previewSkillTickets`
    : `${url}/record/previewSkillTickets`;

  const payload = userId && !isGuest
    ? { userId, ...body }
    : { ...body, completionCount: 1 };

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error('Failed to preview skill tickets');
  }

  return response.json();
}

export async function awardSkillTickets(userId, completionState) {
  const response = await fetch(`${url}/users/awardSkillTickets`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      userId,
      ...buildTicketRequestBody(completionState),
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to award skill tickets');
  }

  return response.json();
}

export async function updateUserStreak(userId) {
  try {
    const response = await fetch(`${url}/users/updateStreak`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      throw new Error('Failed to update streak');
    }

    return response.json();
  } catch (error) {
    console.error('Error updating streak:', error);
    return null;
  }
}

/**
 * @param {{
 *   userId: string,
 *   completionState: object,
 *   circusPeanuts: number,
 *   startingTickets: number,
 *   ticketBreakdown?: object,
 *   themeMeta?: { bigTopTheme?: boolean, theme?: string },
 * }} params
 */
export function buildProgressFeedbackNavigationState({
  userId,
  completionState,
  circusPeanuts,
  startingTickets,
  ticketBreakdown,
  themeMeta = {},
}) {
  return {
    startingTickets,
    circusPeanuts,
    ticketBreakdown,
    unit: completionState?.unit,
    topic: completionState?.topic,
    userId,
    skillId: completionState?.skillId,
    sessionId: completionState?.sessionId ?? null,
    fullSkillPath: completionState?.fullSkillPath,
    skill: completionState?.skill,
    questionsCorrect: completionState?.questionsCorrect,
    questionsAttempted: completionState?.questionsAttempted,
    totalTime: completionState?.totalTime,
    questionsStreak: completionState?.questionsStreak,
    longestStreak: completionState?.longestStreak,
    bigTopTheme: themeMeta.bigTopTheme ?? completionState?.bigTopTheme,
    theme: themeMeta.theme ?? completionState?.theme,
  };
}

/**
 * Record completion, update streak/tickets, return progressFeedback navigation state.
 */
export async function claimSkillReward({
  userId,
  completionState,
  themeMeta = {},
  recordCompletion = false,
}) {
  if (recordCompletion && completionState?.skillId) {
    const action = setAction('skillCompleted', completionState, userId);
    await recordAction(action);
  }

  await updateUserStreak(userId);

  const award = await awardSkillTickets(userId, completionState);

  return buildProgressFeedbackNavigationState({
    userId,
    completionState,
    circusPeanuts: award.ticketsAwarded,
    startingTickets: award.startingTickets,
    ticketBreakdown: award.breakdown,
    themeMeta,
  });
}

/**
 * @deprecated Use previewSkillTickets() — server-side calculation only.
 */
export function calculateCircusPeanuts() {
  console.warn('calculateCircusPeanuts is deprecated; use previewSkillTickets instead.');
  return 0;
}
